import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause, X } from 'lucide-react';
import { resolveImageUrl } from '../utils/imageUrl';

const ProductGallery = ({ product }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const videoRef = useRef(null);
  const galleryRef = useRef(null);

  // Build media items from product data
  const getMediaItems = () => {
    const items = [];
    
    // Check for new media array first
    if (product.media && product.media.length > 0) {
      // Sort by order and return
      const sortedMedia = [...product.media].sort((a, b) => a.order - b.order);
      sortedMedia.forEach(m => {
        items.push({
          url: resolveImageUrl(m.url),
          type: m.type || 'image',
          order: m.order
        });
      });
    } else {
      // Fallback to legacy image/images fields
      if (product.image) {
        items.push({ url: resolveImageUrl(product.image), type: 'image', order: 0 });
      }
      if (product.images && product.images.length > 0) {
        product.images.forEach((img, idx) => {
          // Avoid duplicate if image is same as main
          if (img !== product.image) {
            items.push({ url: resolveImageUrl(img), type: 'image', order: idx + 1 });
          }
        });
      }
    }
    
    return items;
  };

  const mediaItems = getMediaItems();
  const hasMultipleItems = mediaItems.length > 1;

  // Touch handlers for swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0 && currentIndex < mediaItems.length - 1) {
        // Swipe left - next
        setCurrentIndex(currentIndex + 1);
      } else if (diff < 0 && currentIndex > 0) {
        // Swipe right - previous
        setCurrentIndex(currentIndex - 1);
      }
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < mediaItems.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Handle video play/pause
  const toggleVideo = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  // Reset video playing state when changing slides
  useEffect(() => {
    setIsVideoPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  }, [currentIndex]);

  // If no media, show placeholder
  if (mediaItems.length === 0) {
    return (
      <div className="aspect-[4/3] sm:aspect-square rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
        <span className="text-gray-400">No image available</span>
      </div>
    );
  }

  const currentMedia = mediaItems[currentIndex];

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Main Display */}
      <div 
        ref={galleryRef}
        className="relative aspect-[4/3] sm:aspect-square rounded-lg overflow-hidden bg-gray-100 select-none"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        data-testid="product-gallery"
      >
        {currentMedia.type === 'video' ? (
          <div className="relative w-full h-full">
            <video
              ref={videoRef}
              src={currentMedia.url}
              className="w-full h-full object-cover"
              loop
              playsInline
              onClick={toggleVideo}
              onPlay={() => setIsVideoPlaying(true)}
              onPause={() => setIsVideoPlaying(false)}
            />
            {/* Video Play/Pause Overlay */}
            <button
              onClick={toggleVideo}
              className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 hover:bg-opacity-30 transition-all"
              data-testid="video-toggle-btn"
            >
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white bg-opacity-90 flex items-center justify-center shadow-lg">
                {isVideoPlaying ? (
                  <Pause className="w-8 h-8 sm:w-10 sm:h-10 text-gray-800" />
                ) : (
                  <Play className="w-8 h-8 sm:w-10 sm:h-10 text-gray-800 ml-1" />
                )}
              </div>
            </button>
          </div>
        ) : (
          <img
            src={currentMedia.url}
            alt={`${product.name} - Image ${currentIndex + 1}`}
            className="w-full h-full object-cover cursor-pointer"
            draggable={false}
            onClick={() => setIsLightboxOpen(true)}
          />
        )}

        {/* Navigation Arrows - Desktop */}
        {hasMultipleItems && (
          <>
            <button
              onClick={goToPrevious}
              disabled={currentIndex === 0}
              className={`absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center transition-all hidden sm:flex ${
                currentIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-100'
              }`}
              data-testid="gallery-prev-btn"
            >
              <ChevronLeft className="w-6 h-6 text-gray-800" />
            </button>
            <button
              onClick={goToNext}
              disabled={currentIndex === mediaItems.length - 1}
              className={`absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center transition-all hidden sm:flex ${
                currentIndex === mediaItems.length - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-100'
              }`}
              data-testid="gallery-next-btn"
            >
              <ChevronRight className="w-6 h-6 text-gray-800" />
            </button>
          </>
        )}

        {/* Slide Counter */}
        {hasMultipleItems && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm">
            {currentIndex + 1} / {mediaItems.length}
          </div>
        )}

        {/* Swipe Hint for Mobile */}
        {hasMultipleItems && (
          <div className="absolute bottom-12 left-1/2 -translate-x-1/2 text-white text-xs bg-black bg-opacity-40 px-2 py-1 rounded sm:hidden">
            Swipe to see more
          </div>
        )}
      </div>

      {/* Thumbnail Strip */}
      {hasMultipleItems && (
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {mediaItems.map((item, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`relative flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 transition-all ${
                index === currentIndex 
                  ? 'border-yellow-500 ring-2 ring-yellow-200' 
                  : 'border-gray-200 hover:border-gray-400'
              }`}
              data-testid={`thumbnail-${index}`}
            >
              {item.type === 'video' ? (
                <div className="relative w-full h-full">
                  <video
                    src={item.url}
                    className="w-full h-full object-cover"
                    muted
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                    <Play className="w-5 h-5 text-white" />
                  </div>
                </div>
              ) : (
                <img
                  src={item.url}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Lightbox Modal */}
      {isLightboxOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
          onClick={() => setIsLightboxOpen(false)}
        >
          {/* Close Button */}
          <button
            onClick={() => setIsLightboxOpen(false)}
            className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors z-10"
            aria-label="Close lightbox"
            data-testid="lightbox-close-btn"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          {/* Image */}
          <div 
            className="max-w-[90vw] max-h-[90vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={mediaItems[currentIndex].url}
              alt={`${product.name} - Full view`}
              className="max-w-full max-h-[90vh] object-contain"
            />

            {/* Navigation in lightbox */}
            {hasMultipleItems && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                  disabled={currentIndex === 0}
                  className={`absolute left-2 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all ${
                    currentIndex === 0 ? 'opacity-30 cursor-not-allowed' : ''
                  }`}
                >
                  <ChevronLeft className="w-8 h-8 text-white" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); goToNext(); }}
                  disabled={currentIndex === mediaItems.length - 1}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all ${
                    currentIndex === mediaItems.length - 1 ? 'opacity-30 cursor-not-allowed' : ''
                  }`}
                >
                  <ChevronRight className="w-8 h-8 text-white" />
                </button>
              </>
            )}

            {/* Slide counter in lightbox */}
            {hasMultipleItems && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-60 text-white px-4 py-2 rounded-full text-sm">
                {currentIndex + 1} / {mediaItems.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductGallery;
